/**
 * Pure presentation helpers for the PREBUILT catalog cards — the JSX-free half so
 * the blurb/detail extraction is unit-tested without a DOM. A template's `persona`
 * and `appearance` are opaque jsonb records (the server owns their shape), so
 * every read here is defensive: a missing or non-string field yields an empty
 * string, never `undefined` rendered into the DOM or a thrown cast.
 *
 * No domain logic: this never decides price, availability, or consistency — the
 * catalog listing is display-only and the clone is the edge's job.
 */
import type { PrebuiltTemplateView } from '../../api/types.ts';

/** Read a string field off an opaque jsonb record, or `''`. */
function text(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' ? value.trim() : '';
}

/** Her one-line pitch — the template's backstory, or `''` when unset. */
export function templateBlurb(template: PrebuiltTemplateView): string {
  return text(template.persona, 'backstory');
}

/**
 * The short "hair · eyes · body" line under her name. Built by joining only the
 * appearance fields that are actually present, so a sparsely-filled template
 * renders a shorter line rather than stray separators.
 */
export function templateLook(template: PrebuiltTemplateView): string {
  return ['hair', 'eyes', 'body']
    .map((key) => text(template.appearance, key))
    .filter((part) => part.length > 0)
    .join(' · ');
}
